import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NotePencil, ArrowSquareOut } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * Iter 85.38 · Related Meeting Notes list for an Activity Briefing.
 *
 * Surfaces the meeting notes that carry `activity_briefing_id ===
 * activity.id` so an advisor can see the complete working record
 * around a discrete activity (planning call → prep session → staff
 * coordination → post-event debrief → follow-up conversation). These
 * are the SAME canonical Meeting Notes stored in `db.notes` — no
 * copies, no parallel domain.
 *
 * Clicking a related note deep-links via `/notes?open=<id>` so the
 * standard Note Viewer opens on the destination page. That closes the
 * current activity viewer via the platform's dialog dismissal so the
 * partner lands cleanly on the target meeting note.
 *
 * @param {string} activityId  The Activity Briefing note id.
 */
export default function RelatedMeetingNotes({ activityId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activityId) return;
    let alive = true;
    setLoading(true);
    api
      .get("/notes", { params: { activity_briefing_id: activityId } })
      .then((r) => {
        if (!alive) return;
        // Defensive filter: the server already scopes by
        // activity_briefing_id, but drop the activity itself if it
        // somehow slipped in (should never happen — activity briefings
        // never self-reference).
        const rows = (r.data || []).filter((n) => n.id !== activityId);
        setNotes(rows);
      })
      .catch(() => setNotes([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [activityId]);

  if (loading) return null;
  if (notes.length === 0) {
    // Empty state kept intentionally quiet — a fresh activity with no
    // linked meeting notes yet is normal. Advisors will attach meeting
    // notes to it as the activity's arc unfolds.
    return (
      <div className="border border-dashed border-border p-4" data-testid="related-meeting-notes-empty">
        <div className="flex items-center gap-2 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground">
          <NotePencil size={12} weight="bold" /> Related meeting notes
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          No meeting notes attached to this activity yet. Link a meeting note by picking this activity in the composer.
        </div>
      </div>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="border border-border" data-testid="related-meeting-notes">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 text-[0.6875rem] font-mono uppercase tracking-widest text-brass">
        <NotePencil size={12} weight="bold" /> Related meeting notes · {notes.length}
      </div>
      <ul className="divide-y divide-border">
        {notes.map((n) => (
          <li key={n.id}>
            <Link
              to={`/notes?open=${n.id}`}
              className="flex items-start justify-between gap-4 px-4 py-3 hover:bg-secondary/30 group"
              data-testid={`related-meeting-note-${n.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate group-hover:text-primary">{n.title}</div>
                <div className="mt-1 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                  {n.meeting_date && <span>{fmtDate(n.meeting_date)}</span>}
                  {n.author_name && <span>· {n.author_name}</span>}
                  {(n.attendees || []).length > 0 && (
                    <span>· {n.attendees.length} attendee{n.attendees.length === 1 ? "" : "s"}</span>
                  )}
                </div>
              </div>
              <ArrowSquareOut size={14} className="text-muted-foreground group-hover:text-primary shrink-0 mt-1" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
